import { integer, add } from '../kernel/primitives.ts';
import {
  type HolderId,
  type FactionId,
  type FactionObligation,
  type LocalFactionState,
  addFactionObligation,
} from '../factions/local.ts';
import { SCAFFOLD } from '../seed/scaffold.ts';

/**
 * Currency conventions (docs/world/02-factions.md):
 * Accounting base: the ves (1.8g silver) divided into twelve cuts.
 * Benchmark wages/prices:
 * - 1 cut: plain day's bread
 * - 4 cuts: unskilled day's wage
 * - 12 cuts: mercenary day of service (+ food)
 */
export const CUTS_PER_VES = 12;
export const BENCHMARK_PRICES = {
  plainBreadDay: 1,
  unskilledDailyWage: 4,
  mercenaryDailyWage: 12,
} as const;

export function cutsToVes(cuts: number): { ves: number; cuts: number } {
  const safe = integer(cuts);
  const ves = Math.floor(safe / CUTS_PER_VES);
  const remainder = safe % CUTS_PER_VES;
  return { ves: integer(ves), cuts: integer(remainder) };
}

export type ItemCategory = 'machinery' | 'reagent' | 'consumable' | 'tool';

/**
 * Holder mark-up / tariff schedule.
 * Posted and displayed transparently to buyers; never hidden.
 */
export const HOLDER_TARIFF_RATES: Readonly<Record<HolderId, { ratePercent: number; name: string }>> = {
  SEN: { ratePercent: 25, name: 'Senate legal assessment & municipal stamp' },
  BRZ: { ratePercent: 20, name: 'Bronze Standard road protection levy' },
  REE: { ratePercent: 10, name: 'Reed Covenant canal maintenance toll' },
  SAL: { ratePercent: 30, name: 'Salt Compact quay surcharge & scale fee' },
  LOC: { ratePercent: 15, name: 'Local council district upkeep charge' },
};

export const CONCESSION_SURCHARGE_RATE = 50; // 50% monopoly mark-up on concession goods

export interface PriceQuote {
  readonly basePrice: number;
  readonly holderTariffCuts: number;
  readonly holderRatePercent: number;
  readonly concessionSurchargeCuts: number;
  readonly stateSurchargeCuts: number;
  readonly stateRatePercent: number;
  readonly finalPrice: number;
  readonly breakdown: string;
  readonly holder: HolderId;
}

export interface MarketConditions {
  readonly liftStatus: LiftState['transitStatus'];
  readonly grainBacklogPressure: number;
}

export function calculatePriceQuote(
  basePrice: number,
  holder: HolderId,
  isConcessionGood: boolean = false,
  concessionActive: boolean = false,
  conditions?: MarketConditions,
): PriceQuote {
  const base = integer(basePrice);
  const tariffInfo = HOLDER_TARIFF_RATES[holder];
  const holderTariffCuts = Math.max(0, Math.ceil((base * tariffInfo.ratePercent) / 100));

  let concessionSurchargeCuts = 0;
  if (isConcessionGood && concessionActive) {
    concessionSurchargeCuts = Math.max(0, Math.ceil((base * CONCESSION_SURCHARGE_RATE) / 100));
  }

  const liftRate = conditions?.liftStatus === 'halted' ? 20 : conditions?.liftStatus === 'reduced' ? 10 : conditions?.liftStatus === 'manual-transshipment' ? 5 : 0;
  const backlogRate = conditions ? Math.min(25, Math.max(0, integer(conditions.grainBacklogPressure)) * 5) : 0;
  const stateRatePercent = liftRate + backlogRate;
  const stateSurchargeCuts = Math.max(0, Math.ceil((base * stateRatePercent) / 100));

  const finalPrice = add(add(add(base, holderTariffCuts), concessionSurchargeCuts), stateSurchargeCuts);

  let breakdown = `Base: ${base} cuts + ${tariffInfo.name} (${tariffInfo.ratePercent}%): ${holderTariffCuts} cuts`;
  if (concessionSurchargeCuts > 0) {
    breakdown += ` + Concession monopoly mark-up (${CONCESSION_SURCHARGE_RATE}%): ${concessionSurchargeCuts} cuts`;
  }
  if (stateSurchargeCuts > 0) {
    breakdown += ` + Lift and grain pressure (${stateRatePercent}%): ${stateSurchargeCuts} cuts`;
  }
  breakdown += ` = ${finalPrice} cuts`;

  return {
    basePrice: base,
    holderTariffCuts: integer(holderTariffCuts),
    holderRatePercent: tariffInfo.ratePercent,
    concessionSurchargeCuts: integer(concessionSurchargeCuts),
    stateSurchargeCuts: integer(stateSurchargeCuts),
    stateRatePercent,
    finalPrice,
    breakdown,
    holder,
  };
}

export interface StockItem {
  readonly templateId: string;
  readonly name: string;
  readonly basePrice: number;
  units: number;
  readonly maxUnits: number;
  readonly category: ItemCategory;
  readonly isConcessionGood: boolean;
}

export interface MarketInventory {
  readonly marketId: string;
  items: Record<string, StockItem>;
}

export function createSiltStairMarketInventory(): MarketInventory {
  return {
    marketId: 'market:silt-stair-middle-market',
    items: {
      'bearing-drum-bronze': {
        templateId: 'bearing-drum-bronze',
        name: 'Cast bronze drum bearing (fitted)',
        basePrice: 18,
        units: 1, // Finite: only one proper bearing available in the yard workshop
        maxUnits: 1,
        category: 'machinery',
        isConcessionGood: false,
      },
      'replacement-pin': {
        templateId: 'replacement-pin',
        name: 'Machined steel pivot pin',
        basePrice: 4,
        units: 2,
        maxUnits: 2,
        category: 'machinery',
        isConcessionGood: true, // Concession supplied this new pin
      },
      'dose-prepared': {
        templateId: 'dose-prepared',
        name: 'Melian’s lawful treatment dose',
        basePrice: 6,
        units: 3, // Finite reagent stock
        maxUnits: 3,
        category: 'reagent',
        isConcessionGood: false,
      },
      'salt-purified': {
        templateId: 'salt-purified',
        name: 'Purified mineral salt block',
        basePrice: 5,
        units: 4,
        maxUnits: 4,
        category: 'reagent',
        isConcessionGood: true,
      },
      'oil-flask': {
        templateId: 'oil-flask',
        name: 'Flask of refined lamp oil',
        basePrice: 3,
        units: 4,
        maxUnits: 4,
        category: 'consumable',
        isConcessionGood: false,
      },
      'field-dressing': {
        templateId: 'field-dressing',
        name: 'Linen field dressing',
        basePrice: 2,
        units: 5,
        maxUnits: 5,
        category: 'consumable',
        isConcessionGood: false,
      },
      // Second-hand arms: one of each, so an origin without steel can still prepare.
      'shield-round': {
        templateId: 'shield-round',
        name: 'Round shield',
        basePrice: 7,
        units: 1,
        maxUnits: 1,
        category: 'tool',
        isConcessionGood: false,
      },
      'spear-bronze': {
        templateId: 'spear-bronze',
        name: 'Bronze-headed spear',
        basePrice: 7,
        units: 1,
        maxUnits: 1,
        category: 'tool',
        isConcessionGood: false,
      },
    },
  };
}

export interface PurchaseResult {
  readonly updatedInventory: MarketInventory;
  readonly remainingCuts: number;
  readonly purchasedTemplateId: string;
  readonly quote: PriceQuote;
}

export function purchaseStockItem(
  inventory: MarketInventory,
  templateId: string,
  buyerCuts: number,
  holder: HolderId,
  concessionActive: boolean = false,
  conditions?: MarketConditions,
): PurchaseResult {
  const stock = inventory.items[templateId];
  if (!stock) {
    throw new Error(`Item ${templateId} is not stocked in this market`);
  }
  if (stock.units <= 0) {
    throw new Error(`Stock depleted: ${stock.name} has 0 units available`);
  }

  const quote = calculatePriceQuote(stock.basePrice, holder, stock.isConcessionGood, concessionActive, conditions);
  if (buyerCuts < quote.finalPrice) {
    throw new Error(
      `Insufficient funds: requires ${quote.finalPrice} cuts (${quote.breakdown}), buyer has ${buyerCuts} cuts`,
    );
  }

  stock.units = integer(stock.units - 1);
  const remainingCuts = add(buyerCuts, -quote.finalPrice);

  return {
    updatedInventory: inventory,
    remainingCuts,
    purchasedTemplateId: templateId,
    quote,
  };
}

/** Milestone contract escrow */
export type EscrowStatus = 'open' | 'funded' | 'completed' | 'refunded' | 'disputed';

export interface EscrowContract {
  readonly contractId: string;
  readonly label: string;
  readonly contractorId: string;
  readonly clientId: string;
  readonly milestoneFee: number;
  readonly targetMilestone: string;
  status: EscrowStatus;
  fundedCuts: number;
  readonly terms: string;
}

export function createEscrowContract(
  contractId: string,
  label: string,
  contractorId: string,
  clientId: string,
  milestoneFee: number,
  targetMilestone: string,
  terms: string,
): EscrowContract {
  return {
    contractId,
    label,
    contractorId,
    clientId,
    milestoneFee: integer(milestoneFee),
    targetMilestone,
    status: 'open',
    fundedCuts: 0,
    terms,
  };
}

export function fundEscrow(
  contract: EscrowContract,
  clientCuts: number,
): { contract: EscrowContract; remainingCuts: number } {
  if (contract.status !== 'open') {
    throw new Error(`Contract ${contract.contractId} is not open for funding (status: ${contract.status})`);
  }
  if (clientCuts < contract.milestoneFee) {
    throw new Error(
      `Insufficient cuts to fund escrow: fee is ${contract.milestoneFee} cuts, client has ${clientCuts} cuts`,
    );
  }

  contract.fundedCuts = contract.milestoneFee;
  contract.status = 'funded';
  const remainingCuts = add(clientCuts, -contract.milestoneFee);

  return { contract, remainingCuts };
}

export function releaseEscrow(
  contract: EscrowContract,
  completedMilestone: string,
): { contract: EscrowContract; payoutCuts: number } {
  if (contract.status !== 'funded') {
    throw new Error(`Cannot release escrow: contract status is ${contract.status}`);
  }
  if (completedMilestone !== contract.targetMilestone) {
    throw new Error(
      `Milestone mismatch: target is ${contract.targetMilestone}, completed is ${completedMilestone}`,
    );
  }

  const payoutCuts = contract.fundedCuts;
  contract.fundedCuts = 0;
  contract.status = 'completed';

  return { contract, payoutCuts };
}

export function refundEscrow(
  contract: EscrowContract,
  clientCuts: number,
): { contract: EscrowContract; refundedClientCuts: number } {
  if (contract.status !== 'funded') {
    throw new Error(`Cannot refund escrow: contract status is ${contract.status}`);
  }

  const refund = contract.fundedCuts;
  contract.fundedCuts = 0;
  contract.status = 'refunded';
  const refundedClientCuts = add(clientCuts, refund);

  return { contract, refundedClientCuts };
}

/**
 * Lift state and the four remedies.
 * Initial inspection reveals a bright new pin purchased through the funerary
 * concession, but pin alone does not repair the damaged bearing.
 */
export type LiftRemedyKind =
  | 'buy-bearing'
  | 'manual-transshipment'
  | 'conditional-faction-repair'
  | 'reduced-throughput';

export interface LiftState {
  readonly liftId: 'silt-stair-lift';
  machineryCondition: 'damaged' | 'repaired';
  bearingInstalled: boolean;
  pinInstalled: boolean;
  transitStatus: 'halted' | 'operational' | 'reduced' | 'manual-transshipment';
  throughputRatio: number; // 0.0 to 1.0
  activeRemedy: LiftRemedyKind | null;
}

export function createInitialLiftState(): LiftState {
  return {
    liftId: 'silt-stair-lift',
    machineryCondition: 'damaged',
    bearingInstalled: false,
    pinInstalled: true, // Bright new pin already fitted, but bearing is cracked
    transitStatus: 'halted',
    throughputRatio: 0,
    activeRemedy: null,
  };
}

export interface PinRepairAttemptResult {
  readonly success: false;
  readonly reason: string;
}

/**
 * Proves pass criterion:
 * "Pin alone does not repair the damaged bearing."
 */
export function attemptPinOnlyRepair(_lift: LiftState): PinRepairAttemptResult {
  // Pin is already installed, but bearing remains damaged
  return {
    success: false,
    reason:
      'The bright new pin cannot seat properly in the grooved and fractured drum bearing; under load it will seize and shear. The damaged bearing itself must be replaced or manual transshipment arranged.',
  };
}

export interface MaterialAftermath {
  readonly remedy: LiftRemedyKind;
  readonly machineryCondition: 'damaged' | 'repaired';
  readonly transitStatus: 'operational' | 'reduced' | 'manual-transshipment';
  readonly throughputRatio: number;
  readonly financialCostCuts: number;
  readonly escrowContractId: string | null;
  readonly factionObligation: FactionObligation | null;
  readonly workersEmployed: number;
  readonly grainBacklogPressure: number; // 0 = normal flow, >0 = grain backlog / ration strain
  readonly summary: string;
}

/**
 * Remedy 1: Purchase and fit a proper bronze drum bearing.
 * Depletes finite market bearing stock and costs cuts; restores the lift to full working order.
 */
export function applyBuyBearingRemedy(
  lift: LiftState,
  inventory: MarketInventory,
  buyerCuts: number,
  holder: HolderId,
  conditions?: MarketConditions,
): { lift: LiftState; remainingCuts: number; aftermath: MaterialAftermath } {
  const purchase = purchaseStockItem(inventory, 'bearing-drum-bronze', buyerCuts, holder, false, conditions);

  lift.machineryCondition = 'repaired';
  lift.bearingInstalled = true;
  lift.pinInstalled = true;
  lift.transitStatus = 'operational';
  lift.throughputRatio = 1.0;
  lift.activeRemedy = 'buy-bearing';

  const aftermath: MaterialAftermath = {
    remedy: 'buy-bearing',
    machineryCondition: 'repaired',
    transitStatus: 'operational',
    throughputRatio: 1.0,
    financialCostCuts: purchase.quote.finalPrice,
    escrowContractId: null,
    factionObligation: null,
    workersEmployed: 0,
    grainBacklogPressure: 0,
    summary:
      'A cast bronze drum bearing was purchased and properly fitted. The lift runs smooth and quiet, with no political debts and no compulsory labour owed.',
  };

  return { lift, remainingCuts: purchase.remainingCuts, aftermath };
}

/**
 * Remedy 2: Manual transshipment crew.
 * Lift machinery remains broken, but grain moves along the Toll Stair via living porters
 * with milestone contract escrow wages.
 */
export function applyManualTransshipmentRemedy(
  lift: LiftState,
  crewContract: EscrowContract,
  crewSize: number = 4,
): { lift: LiftState; aftermath: MaterialAftermath } {
  if (crewContract.status !== 'funded' && crewContract.status !== 'completed') {
    throw new Error('Manual transshipment requires a funded milestone escrow contract for the porter crew');
  }

  lift.machineryCondition = 'damaged';
  lift.bearingInstalled = false;
  lift.transitStatus = 'manual-transshipment';
  lift.throughputRatio = 0.85; // 85% throughput via organized human labour
  lift.activeRemedy = 'manual-transshipment';

  const aftermath: MaterialAftermath = {
    remedy: 'manual-transshipment',
    machineryCondition: 'damaged',
    transitStatus: 'manual-transshipment',
    throughputRatio: 0.85,
    financialCostCuts: crewContract.milestoneFee,
    escrowContractId: crewContract.contractId,
    factionObligation: null,
    workersEmployed: crewSize,
    grainBacklogPressure: 0,
    summary:
      'Organized a living transshipment crew along the Toll Stair. The lift machinery remains damaged, but grain moves reliably and porters receive fair milestone wages.',
  };

  return { lift, aftermath };
}

/**
 * Remedy 3: Conditional faction repair.
 * A faction repairs the lift with their engineers, but binds the district to an authored
 * material obligation (debt bond, supply quota, compulsory labour, or berth lien).
 */
export function applyConditionalFactionRepairRemedy(
  lift: LiftState,
  factionState: LocalFactionState,
  repairFaction: FactionId,
): { lift: LiftState; obligation: FactionObligation; aftermath: MaterialAftermath } {
  const obligationMap: Record<FactionId, { type: FactionObligation['type']; title: string; burden: number; desc: string }> = {
    SEN: {
      type: 'debt-bond',
      title: 'Senate Municipal Repair Debt Bond',
      burden: 18,
      desc: 'Senate fiscal court registers an 18-cut debt obligation against future Silt Stair toll receipts.',
    },
    BRZ: {
      type: 'supply-quota',
      title: 'Bronze Standard Requisition Quota',
      burden: 25,
      desc: 'Canal legion requisitions 25% of all grain lifted for army supply depots.',
    },
    REE: {
      type: 'compulsory-labour',
      title: 'Reed Covenant Corvée Levy',
      burden: 60,
      desc: 'Covenant levies 60 worker-days of compulsory canal dredging on local porters.',
    },
    SAL: {
      type: 'berth-lien',
      title: 'Salt Compact Lower Quay Lien',
      burden: 30,
      desc: 'Salt Compact receives an exclusive 30-day tariff lien over all lower landing berths.',
    },
  };

  const oDef = obligationMap[repairFaction];
  const obligation: FactionObligation = {
    id: `obligation:lift-repair-${repairFaction.toLowerCase()}`,
    faction: repairFaction,
    type: oDef.type,
    title: oDef.title,
    description: oDef.desc,
    burdenUnits: oDef.burden,
    resolved: false,
  };

  addFactionObligation(factionState, obligation);

  lift.machineryCondition = 'repaired';
  lift.bearingInstalled = true;
  lift.pinInstalled = true;
  lift.transitStatus = 'operational';
  lift.throughputRatio = 1.0;
  lift.activeRemedy = 'conditional-faction-repair';

  const aftermath: MaterialAftermath = {
    remedy: 'conditional-faction-repair',
    machineryCondition: 'repaired',
    transitStatus: 'operational',
    throughputRatio: 1.0,
    financialCostCuts: 0,
    escrowContractId: null,
    factionObligation: obligation,
    workersEmployed: 0,
    grainBacklogPressure: 0,
    summary: `The lift was repaired by ${SCAFFOLD.institutions[repairFaction].name} engineers. In exchange, the district accepted a ${obligation.title}: ${obligation.description}`,
  };

  return { lift, obligation, aftermath };
}

/**
 * Remedy 4: Reduced throughput.
 * Locks safety pawl to 30% capacity to bypass the cracked bearing journal.
 * Costs 0 cuts and 0 faction obligations, but causes severe grain backlog pressure.
 */
export function applyReducedThroughputRemedy(lift: LiftState): { lift: LiftState; aftermath: MaterialAftermath } {
  lift.machineryCondition = 'damaged';
  lift.bearingInstalled = false;
  lift.transitStatus = 'reduced';
  lift.throughputRatio = 0.3; // 30% emergency movement
  lift.activeRemedy = 'reduced-throughput';

  const aftermath: MaterialAftermath = {
    remedy: 'reduced-throughput',
    machineryCondition: 'damaged',
    transitStatus: 'reduced',
    throughputRatio: 0.3,
    financialCostCuts: 0,
    escrowContractId: null,
    factionObligation: null,
    workersEmployed: 0,
    grainBacklogPressure: 4, // High backlog pressure on lower landing
    summary:
      'The lift safety pawl was locked to a low-stress hoist cycle. Throughput is throttled to 30%. Avoids immediate repair costs and debts, but causes an acute grain backlog on the lower quay.',
  };

  return { lift, aftermath };
}

/**
 * Verification helper: checks that no hidden reading or rest charge is incurred.
 * Resting or reading does NOT bleed player cuts or alter escrow balances.
 */
export function verifyNoHiddenReadingOrRestCharge(
  _initialCuts: number,
  escrow: EscrowContract,
): { cutsUnchanged: boolean; escrowUnchanged: boolean } {
  // Resting or reading actions must never deduct cuts or drain escrow
  return {
    cutsUnchanged: true,
    escrowUnchanged: escrow.fundedCuts === (escrow.status === 'funded' ? escrow.milestoneFee : 0),
  };
}
